import React from 'react';
import styled from 'styled-components';
import { vars } from '../../theme';
import HBText from '../HBText/HBText';
import HBLinkButton from '../HBLinkButton/HBLinkButton';
import type { HBTableFiltersProps } from './HBTable.types';
import { TABLE_Z_INDEX } from './HBTable.styles';
import { AnimatePresence, motion } from 'framer-motion';
import { Close16 } from '@honeybook/icons';
import HBIconButton from '../HBIconButton/HBIconButton';

function HBTableFloatingBulkActions<T>({
    table,
    showRowSelection
}: HBTableFiltersProps<T>) {
    const { isInitialLoading, bulkActions, helper } = table.options.meta;

    if (isInitialLoading || (!bulkActions && !showRowSelection)) {
        return null;
    }

    return (
        <AnimatePresence>
            {helper.isRowsSelected && (
                <Container
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.06, ease: 'easeInOut' }}
                >
                    <HBText
                        type="body14"
                        color="text.secondary"
                        ellipsis
                        testId={`${table.options.meta.testIdPrefix}-selection-text`}
                    >
                        {isInitialLoading
                            ? 'Loading...'
                            : `${
                                  helper.selectedRowCount
                              } of ${table.getRowCount()} selected`}
                    </HBText>
                    <HBLinkButton
                        linkButtonTheme="neutral"
                        size="sm"
                        testIdName="select-all"
                        onClick={() =>
                            table.setRowSelection(
                                helper.isAllRowsSelected ? {} : { all: true }
                            )
                        }
                    >
                        {helper.isAllRowsSelected
                            ? 'Deselect all'
                            : 'Select all'}
                    </HBLinkButton>
                    {bulkActions && (
                        <>
                            <Divider />
                            {bulkActions.map(
                                ({ text, icon: Icon, onClick }) => (
                                    <HBLinkButton
                                        key={text}
                                        testIdName={`bulk-${text}`}
                                        linkButtonTheme="neutral"
                                        size="sm"
                                        icon={<Icon />}
                                        onClick={() => {
                                            const clearSelection = () =>
                                                table.setRowSelection({});

                                            const { rowSelection } =
                                                table.getState();
                                            const rowSelectionCopy = {
                                                ...rowSelection
                                            };
                                            delete rowSelectionCopy.all;
                                            const rowIds =
                                                Object.keys(rowSelectionCopy);

                                            if (rowSelection.all) {
                                                onClick(
                                                    { nin: rowIds },
                                                    clearSelection
                                                );
                                            } else {
                                                onClick(
                                                    { in: rowIds },
                                                    clearSelection
                                                );
                                            }
                                        }}
                                    >
                                        {text}
                                    </HBLinkButton>
                                )
                            )}
                            <Divider />
                        </>
                    )}
                    <HBIconButton
                        icon={Close16}
                        margin="0 0 0 -8px"
                        testIdName="deselect-all"
                        label="Deselect all"
                        onClick={() => table.setRowSelection({})}
                    />
                </Container>
            )}
        </AnimatePresence>
    );
}

export default HBTableFloatingBulkActions;

// -------------------------- Styles -------------------------- //

const Container = styled(motion.div)`
    position: sticky;
    align-self: center;
    bottom: 24px;
    z-index: ${TABLE_Z_INDEX.MIDDLE};
    width: fit-content;
    max-width: 100%;
    overflow: auto;
    height: 48px;
    margin: 8px 0 22px;
    padding: 0 8px 0 16px;
    display: flex;
    align-items: center;
    gap: 16px;
    background-color: ${vars.colors.surface.container};
    border: 1px solid ${vars.colors.stroke.secondary};
    border-radius: ${vars.radius.md};
    box-shadow: ${vars.shadow['40']};
`;

const Divider = styled.div`
    width: 1px;
    height: 100%;
    background-color: ${vars.colors.stroke.secondary};
`;
